import {
	Box,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	tableCellClasses,
} from '@mui/material'

import { ICowsList } from 'models/ICattlesData'
import { useNavigate } from 'react-router-dom'
import { COLORS } from 'styles/colors'

import BarchartInfoLogo from 'assets/BarchartInfoLogo'
import { getFatnessColorByTable } from 'components/cattles/fatnessPage/shared/fatnessScoreColor'
import { useEffect, useMemo, useRef, useState } from 'react'
import SelectSmall from 'ui/menu/SelectForm'
import TableSkeleton from 'ui/skeleton/TableSkeleton'
import TooltipHover from 'ui/TooltipHover'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { TypographySecondaryBold16 } from 'ui/typography/TypographySecondaryBold16'
import { ReactComponent as ArrowNext } from '../../../../assets/fill_back_arrow.svg'
import { ReactComponent as TagNumberIcon } from '../../../../assets/icons/tag-icon.svg'
import FatnessTooltipModal from './fatnessTooltip/FatnessTooltip'

const hasNumericWeight = (weight: unknown): boolean =>
	weight != null && weight !== '' && Number.isFinite(Number(weight))

interface IFatnessTableProps {
	cattlesInfo: ICowsList[]
	setActiveFilterTypeSelect: React.Dispatch<React.SetStateAction<number[]>>
	fetchingCowsList: boolean
	fatnessScoreOptions: Array<{ type: number; value: string }>
	emptyMessage: string
	showFilteredEmptyState: boolean
}
const FatnessTable = (props: IFatnessTableProps) => {
	const {
		cattlesInfo,
		setActiveFilterTypeSelect,
		fetchingCowsList,
		fatnessScoreOptions,
		emptyMessage,
		showFilteredEmptyState,
	} = props
	const [isModalOpen, setIsModalOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement | null>(null)
	const navigate = useNavigate()
	const navigateCowPasportPage = (
		tagId: number | string,
		id: number,
		identified?: boolean,
	) => {
		navigate(`/cattles/cow/${encodeURIComponent(String(tagId))}`, {
			state: { cowId: id, identified },
		})
		window.scrollTo(0, 0)
	}
	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTo({ top: 0, behavior: 'auto' })
		}
	}, [cattlesInfo])
	const showWeightColumn = useMemo(
		() => cattlesInfo.some(cow => hasNumericWeight(cow.weight)),
		[cattlesInfo],
	)
	const columnCount = showWeightColumn ? 7 : 6
	return (
		<TableContainer
			ref={containerRef}
			sx={{
				overflowY: 'auto',
				overflowX: 'hidden',
				height: 'calc(100vh - 350px)',
				'&::-webkit-scrollbar': {
					width: 4,
				},
				'&::-webkit-scrollbar-track': {
					backgroundColor: '#DBDBDB',
					borderRadius: 12,
				},
				'&::-webkit-scrollbar-thumb': {
					backgroundColor: COLORS.icon,
					borderRadius: 12,
				},
			}}
		>
			<Table stickyHeader>
				<TableHead
					sx={{
						[`& .${tableCellClasses.root}`]: {
							backgroundColor: '#fff',
							marginBottom: '10%',
						},
					}}
				>
					<TableRow sx={{ width: '100%', height: '50px' }}>
						<TableCell align={'left'} padding={'none'} width={'16%'}>
							<Box
								display={'flex'}
								flexDirection={'row'}
								alignItems={'center'}
								gap={'4px'}
							>
								<TagNumberIcon />
								<TypographySecondary>
									Номер бирки
									<br /> Номер ошейника
								</TypographySecondary>
							</Box>
						</TableCell>
						<TableCell align={'left'} padding={'none'} width={'18%'}>
							<TypographySecondary>Группа</TypographySecondary>
						</TableCell>
						<TableCell
							align={'left'}
							padding={'none'}
							width={showWeightColumn ? '14%' : '18%'}
						>
							<TypographySecondary>День лактации</TypographySecondary>
						</TableCell>
						{showWeightColumn && (
							<TableCell align={'left'} padding={'none'} width={'12%'}>
								<TypographySecondary>Вес</TypographySecondary>
							</TableCell>
						)}
						<TableCell
							align={'left'}
							padding={'none'}
							width={showWeightColumn ? '16%' : '18%'}
						>
							<SelectSmall
								typeArray={fatnessScoreOptions}
								isCheckbox
								isMultiplaySelect
								state={setActiveFilterTypeSelect}
								placeholder={'Упитанность'}
								isClearIcon
								floatingLabel='Упитанность'
								height='40px'
								width='134px'
							/>
						</TableCell>
						<TableCell align={'left'} padding={'none'} width={'22%'}>
							<Box display='flex' alignItems='center' gap='4px'>
								<TypographySecondary>Норма упитанности</TypographySecondary>
								<TooltipHover
									title={
										<span>
											Норма упитанности зависит от дня лактации и
											физиологического периода коровы. Для каждой стадии
											используется свой рекомендуемый диапазон баллов.{' '}
											<span
												style={{
													color: COLORS.main,
													textDecoration: 'underline',
													cursor: 'pointer',
												}}
												onClick={() => setIsModalOpen(true)}
											>
												Подробнее&gt;
											</span>
										</span>
									}
									placement='bottom'
									oneLine={false}
									maxWidth='350px'
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											cursor: 'pointer',
											'&:hover svg': {
												stroke: COLORS.main,
											},
										}}
									>
										<BarchartInfoLogo
											width={'16px'}
											height={'16px'}
											stroke={COLORS.icon}
										/>
									</Box>
								</TooltipHover>
							</Box>
						</TableCell>
						<TableCell align={'left'} padding={'none'} width={'8%'}>
							<TypographySecondary>Действия</TypographySecondary>
						</TableCell>
					</TableRow>
				</TableHead>
				{fetchingCowsList ? (
					<TableBody>
						<TableRow>
							<TableCell
								colSpan={columnCount}
								align={'center'}
								padding={'none'}
							>
								<TableSkeleton
									widthForBody={'100%'}
									heightForBody={'34px'}
									rowCount={36}
									totalHeight={'calc(100vh - 350px)'}
								/>
							</TableCell>
						</TableRow>
					</TableBody>
				) : cattlesInfo.length === 0 ? (
					<TableBody>
						<TableRow
							sx={{
								width: '100%',
								[`& .${tableCellClasses.root}`]: {
									borderBottom: 'none',
									height: '300px',
								},
							}}
						>
							<TableCell
								colSpan={columnCount}
								align={'center'}
								padding={'none'}
							>
								<Stack
									direction='column'
									justifyContent='center'
									alignItems='center'
									gap='8px'
								>
									{showFilteredEmptyState ? (
										<>
											<TypographySecondaryBold16>
												Нет данных
											</TypographySecondaryBold16>
											<TypographySecondary14>
												Нет данных, соответствующих выбранным условиям.
											</TypographySecondary14>
										</>
									) : (
										<TypographySecondary14>
											{emptyMessage}
										</TypographySecondary14>
									)}
								</Stack>
							</TableCell>
						</TableRow>
					</TableBody>
				) : (
					<TableBody>
						{cattlesInfo.map((el, index) => {
							const isErrorLactation =
								el.lactation_length <= 0 || el.lactation_length > 365
							const fatnessTextColor = getFatnessColorByTable(
								el.fatness,
								el.lactation_length,
								el.fatness_status,
							)
							return (
								<TableRow
									key={index}
									sx={{
										height: '34px',
										'&:hover': {
											background: COLORS.mainSecondaryOpacity,
										},
										[`& .${tableCellClasses.root}`]: {
											borderBottom: 'none',
										},
									}}
								>
									<TableCell align={'left'} padding={'none'}>
										<Box
											display={'flex'}
											flexDirection={'row'}
											alignItems={'center'}
											gap={'4px'}
										>
											{el.critical_state && (
												<span
													style={{
														display: 'inline-block',
														borderRadius: '50%',
														backgroundColor: COLORS.error,
														width: '8px',
														height: '8px',
													}}
												></span>
											)}
											<Box display={'flex'} flexDirection={'column'}>
												<TypographyPrimary
													sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}
												>
													{el.tag_id}
												</TypographyPrimary>
												{el.collar_num && (
													<TypographySecondary14
														sx={{ whiteSpace: 'nowrap', lineHeight: 1 }}
													>
														{el.collar_num}
													</TypographySecondary14>
												)}
											</Box>
										</Box>
									</TableCell>
									<TableCell align={'left'} padding={'none'}>
										<TypographyPrimary>{el.group.title}</TypographyPrimary>
									</TableCell>

									<TableCell align={'left'} padding={'none'}>
										<TypographyPrimary
											sx={{
												color: isErrorLactation ? COLORS.error : COLORS.black,
											}}
										>
											{el.lactation_length}{' '}
											<span
												style={{
													fontSize: '12px',
													color: COLORS.secondary,
												}}
											>
												день
											</span>
										</TypographyPrimary>
									</TableCell>

									{showWeightColumn && (
										<TableCell align={'left'} padding={'none'}>
											{hasNumericWeight(el.weight) ? (
												<TypographyPrimary>
													{el.weight}{' '}
													<span
														style={{
															fontSize: '12px',
															color: COLORS.secondary,
														}}
													>
														кг
													</span>
												</TypographyPrimary>
											) : (
												<TypographyPrimary>-</TypographyPrimary>
											)}
										</TableCell>
									)}

									<TableCell align={'left'} padding={'none'}>
										<TypographyPrimary
											sx={{
												color: fatnessTextColor,
											}}
										>
											{el.fatness == null ? (
												'-'
											) : (
												<>
													{el.fatness}{' '}
													<span
														style={{
															fontSize: '12px',
															color: COLORS.secondary,
														}}
													>
														б
													</span>
												</>
											)}
										</TypographyPrimary>
									</TableCell>
									{isErrorLactation ? (
										<TableCell align={'left'} padding={'none'}>
											<TooltipHover
												title={
													<span>
														Данные по дням лактации недействительны.
														Актуализируйте систему управления стадом.
														<br />
														Для корректных расчетов нормы упитанности текущий
														лактационный день коровы не должен превышать 365
														дней.
													</span>
												}
												placement='top'
												oneLine={false}
												maxWidth='350px'
											>
												<Box
													sx={{
														display: 'flex',
														alignItems: 'center',
														cursor: 'pointer',
														'&:hover svg': {
															stroke: COLORS.main,
														},
													}}
												>
													<BarchartInfoLogo
														width={'16px'}
														height={'16px'}
														stroke={COLORS.error}
													/>
												</Box>
											</TooltipHover>
										</TableCell>
									) : (
										<TableCell align={'left'} padding={'none'}>
											<TypographySecondary14>
												{el.fatness_norm}
											</TypographySecondary14>
										</TableCell>
									)}

									<TableCell align={'right'}>
										<TooltipHover
											title='Перейти на страницу коровы'
											placement='bottom-start'
											oneLine
										>
											<Box
												onClick={() =>
													navigateCowPasportPage(
														el.tag_id,
														el.id,
														el.identified,
													)
												}
											>
												<ArrowNext
													style={{
														cursor: 'pointer',
														transform: 'rotate(180deg)',
													}}
													stroke={COLORS.secondary}
												/>
											</Box>
										</TooltipHover>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				)}
			</Table>
			<FatnessTooltipModal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
			/>
		</TableContainer>
	)
}

export default FatnessTable
